/**
 * PasswordResetDialog — the real public endpoint POST /auth/password/reset-request (auth_iam,
 * publicRoute + authLimiter). Enumeration-safe by contract: the server returns the same ack whether
 * or not the e-mail matches, so the UI always shows the same neutral confirmation. No capability
 * gate (anonymous flow). Keyboard-first modal: Esc/overlay to close, Enter to submit.
 */
import { useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, useToast } from "@/ui";
import { api, ApiError } from "@/api";
import { useT } from "@/app";

export interface PasswordResetDialogProps {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
}

export function PasswordResetDialog({ open, onClose, initialEmail = "" }: PasswordResetDialogProps) {
  const { t } = useT();
  const toast = useToast();
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const close = () => {
    setDone(false);
    setSubmitting(false);
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/password/reset-request", { email: email.trim() });
      setDone(true);
      toast.success(t("auth.reset.done"));
    } catch (err) {
      // Rate-limit (429) or transport — never leak account existence; show a generic message.
      const message = err instanceof ApiError && err.httpStatus === 429 ? err.message : t("auth.reset.error");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("auth.reset.title")}
      footer={
        <Button type="button" variant="ghost" onClick={close}>
          {t("action.close")}
        </Button>
      }
    >
      {done ? (
        <p role="status" style={{ margin: 0 }}>
          {t("auth.reset.done")}
        </p>
      ) : (
        <form onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("auth.reset.desc")}</p>
          <FormField label={t("auth.login.email")} required>
            {(p) => (
              <Input
                {...p}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </FormField>
          <Button type="submit" block loading={submitting}>
            {submitting ? t("auth.reset.submitting") : t("auth.reset.submit")}
          </Button>
        </form>
      )}
    </Modal>
  );
}
